import { FC, useState } from "react"
import useAdvertisers from "../hooks/useAdvertisers"
import Title from "antd/lib/typography/Title"
import { Button, InputNumber, Modal, Space, Switch } from "antd"
import { IAdvertiser } from "../api/types"
import { CreateAdvertiser } from "./createAdvertiser"
import { useNavigate } from "react-router-dom"
import { updateAdvertiser } from "../api"
import { DraggableTable, DragHandle, reorderPriorities } from "./DraggableTable"
import { notifications } from "notifications"

const InternalOWAdvertisersPage: FC = () => {
  const { advertisers, updateAdvertiser: updateAdvertiserEntry } = useAdvertisers()
  const [showCreateAdvertiser, setShowCreateAdvertiser] = useState(false)
  const navigate = useNavigate()
  const [patching, setPatching] = useState(false)

  const onPatch = (advertiser: IAdvertiser) => {
    if (patching) return
    setPatching(true)
    const updated = { ...advertiser }
    if (!updated.smallImageUrl && updated.imageUrl) {
      updated.smallImageUrl = updated.imageUrl
    }
    updateAdvertiser(updated).then(result => {
      updateAdvertiserEntry(result)
    }).finally(() => {
      setPatching(false)
    })
  }

  const onPriorityChange = (advertiser: IAdvertiser, newPriority: number | null) => {
    if (newPriority === null || newPriority === advertiser.priority || !advertisers) return
    if (patching) return
    setPatching(true)

    const oldPriority = advertiser.priority
    const updates: IAdvertiser[] = []

    if (oldPriority < newPriority) {
      advertisers.forEach(a => {
        if (a.id === advertiser.id) return
        if (a.priority > oldPriority && a.priority <= newPriority) {
          const updated = { ...a, priority: a.priority - 1 }
          if (!updated.smallImageUrl && updated.imageUrl) {
            updated.smallImageUrl = updated.imageUrl
          }
          updates.push(updated)
        }
      })
    } else {
      advertisers.forEach(a => {
        if (a.id === advertiser.id) return
        if (a.priority >= newPriority && a.priority < oldPriority) {
          const updated = { ...a, priority: a.priority + 1 }
          if (!updated.smallImageUrl && updated.imageUrl) {
            updated.smallImageUrl = updated.imageUrl
          }
          updates.push(updated)
        }
      })
    }

    const updatedAdvertiser = { ...advertiser, priority: newPriority }
    if (!updatedAdvertiser.smallImageUrl && updatedAdvertiser.imageUrl) {
      updatedAdvertiser.smallImageUrl = updatedAdvertiser.imageUrl
    }
    updates.push(updatedAdvertiser)

    Promise.all(updates.map(updateAdvertiser))
      .then(updated => {
        updated.forEach(updateAdvertiserEntry)
      })
      .finally(() => {
        setPatching(false)
      })
  }

  const onDragEnd = (activeId: string, overId: string, activeIndex: number, overIndex: number) => {
    if (patching || !advertisers) return
    setPatching(true)
    reorderPriorities(advertisers, activeIndex, overIndex, updateAdvertiser, updateAdvertiserEntry)
      .finally(() => setPatching(false))
  }

  const onResetPriorities = () => {
    if (patching || !advertisers) return

    const updates: IAdvertiser[] = []
    advertisers.forEach((advertiser, index) => {
      const correctPriority = index + 1
      if (advertiser.priority === correctPriority) return

      const updated: IAdvertiser = {
        ...advertiser,
        priority: correctPriority,
      }

      if (!updated.smallImageUrl && updated.imageUrl) {
        updated.smallImageUrl = updated.imageUrl
      }

      updates.push(updated)
    })

    if (updates.length === 0) {
      notifications.success("Priorities are already correct")

      return
    }

    setPatching(true)
    Promise.all(updates.map(updateAdvertiser))
      .then(updated => {
        updated.forEach(updateAdvertiserEntry)
        notifications.success("Priorities reset successfully")
      })
      .catch(e => {
        notifications.error(e.cause?.message || "Failed to reset priorities")
      })
      .finally(() => {
        setPatching(false)
      })
  }

  const onClose = () => setShowCreateAdvertiser(false)

  const renderTrimmedDescription = (description: string) => {
    if (!description) return ""
    const willTrim = description.length > 100

    return willTrim ? description.substring(0, 100).trim() + "..." : description
  }

  return (
    <div>
      <Modal
        open={showCreateAdvertiser}
        title="Create an Advertiser"
        width="700px"
        footer={null}
        onCancel={onClose}
        style={{ top: "10px" }}
      >
        <CreateAdvertiser onClose={onClose} />
      </Modal>
      <Space
        direction="horizontal"
        style={{
          width: "100%",
          justifyContent: "space-between",
          alignItems: "center",
          paddingTop: 12,
        }}
      >
        <Title level={2}>All Advertisers</Title>
        <Space>
          <Button
            onClick={onResetPriorities}
            disabled={patching || !advertisers || advertisers.length === 0}
          >
            Reset Priorities
          </Button>
          <Button
            type="primary"
            onClick={() => {
              setShowCreateAdvertiser(true)
            }}
          >
            Add an Advertiser
          </Button>
        </Space>
      </Space>
      <Space direction="vertical" style={{ width: "100%" }}>
        <DraggableTable<IAdvertiser>
          dataSource={advertisers}
          rowKey={record => record.id}
          scroll={{ x: true }}
          loading={!advertisers}
          onDragEnd={onDragEnd}
          onRow={record => ({
            style: { cursor: "pointer" },
            onClick: () => {
              navigate(`/internal-offerwall/advertisers/${record.id}`)
            },
          })}
          columns={[
            {
              key: "imageUrl",
              title: "Image",
              dataIndex: "imageUrl",
              render: url => <img src={url} alt="" style={{ width: 48, height: 48 }} />,
            },
            { key: "name", title: "Advertiser Name", dataIndex: "name" },
            {
              key: "description",
              title: "Description",
              dataIndex: "description",
              render: renderTrimmedDescription,
            },
            {
              key: "type",
              title: "Type",
              dataIndex: "type",
              render: type => (type ? type.charAt(0).toUpperCase() + type.slice(1).toLowerCase() : "Partner"),
            },
            {
              key: "regions",
              title: "Regions",
              dataIndex: "geoRegions",
              render: regions => regions.join(", "),
            },
            {
              key: "forMobile",
              title: "For Mobile",
              dataIndex: "forMobile",
              render: forMobile => (forMobile ? "Yes" : "No"),
            },
            {
              key: "active",
              title: "Active",
              dataIndex: "active",
              render: (active, advertiser) => (
                <div onClick={e => e.stopPropagation()}>
                  <Switch
                    onClick={() => onPatch({ ...advertiser, active: !advertiser.active })}
                    disabled={patching}
                    checked={active}
                  />
                </div>
              ),
            },
            {
              key: "isNew",
              title: "New",
              dataIndex: "isNew",
              render: (isNew, advertiser) => (
                <div onClick={e => e.stopPropagation()}>
                  <Switch
                    onClick={() => onPatch({ ...advertiser, isNew: !advertiser.isNew })}
                    disabled={patching}
                    checked={isNew}
                  />
                </div>
              ),
            },
            {
              key: "priority",
              title: "Priority",
              dataIndex: "priority",
              render: (priority, advertiser) => (
                <div onClick={e => e.stopPropagation()} style={{ display: "flex", alignItems: "center" }}>
                  <DragHandle />
                  <InputNumber
                    value={priority}
                    min={1}
                    disabled={patching}
                    controls={false}
                    style={{ width: 60 }}
                    onBlur={e => onPriorityChange(advertiser, parseInt(e.target.value, 10) || priority)}
                    onPressEnter={e => onPriorityChange(advertiser, parseInt((e.target as HTMLInputElement).value, 10) || priority)}
                  />
                </div>
              ),
            },
          ]}
        />
      </Space>
    </div>
  )
}

export default InternalOWAdvertisersPage
